import React from 'react';
import { defaultTableBlock, COLUMN_STYLES, slugifyColumnKey } from './shared';

function moveItem(list, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export default function CustomTablesSectionFields({ section, onChange }) {
  const update = (patch) => onChange({ ...section, ...patch });
  const tables = section.tables || [];

  const updateTable = (tableIdx, patch) => {
    const next = [...tables];
    next[tableIdx] = { ...next[tableIdx], ...patch };
    update({ tables: next });
  };

  const updateColumn = (tableIdx, colIdx, patch) => {
    const table = tables[tableIdx];
    const columns = [...(table.columns || [])];
    const prevKey = columns[colIdx]?.key;
    columns[colIdx] = { ...columns[colIdx], ...patch };

    if (patch.key && patch.key !== prevKey) {
      const rows = (table.rows || []).map((row) => {
        const nextRow = { ...row };
        if (prevKey in nextRow) {
          nextRow[patch.key] = nextRow[prevKey];
          delete nextRow[prevKey];
        }
        return nextRow;
      });
      updateTable(tableIdx, { columns, rows });
      return;
    }

    updateTable(tableIdx, { columns });
  };

  const addColumn = (tableIdx) => {
    const table = tables[tableIdx];
    const key = `col${(table.columns || []).length + 1}`;
    const columns = [...(table.columns || []), { key, label: 'Column', style: 'text' }];
    const rows = (table.rows || []).map((row) => ({ ...row, [key]: row[key] ?? '' }));
    updateTable(tableIdx, { columns, rows });
  };

  const removeColumn = (tableIdx, colIdx) => {
    const table = tables[tableIdx];
    const key = table.columns[colIdx]?.key;
    const columns = table.columns.filter((_, i) => i !== colIdx);
    const rows = (table.rows || []).map((row) => {
      const nextRow = { ...row };
      if (key) delete nextRow[key];
      return nextRow;
    });
    updateTable(tableIdx, { columns, rows });
  };

  const updateRow = (tableIdx, rowIdx, colKey, value) => {
    const table = tables[tableIdx];
    const rows = [...(table.rows || [])];
    rows[rowIdx] = { ...rows[rowIdx], [colKey]: value };
    updateTable(tableIdx, { rows });
  };

  const addRow = (tableIdx) => {
    const table = tables[tableIdx];
    const emptyRow = {};
    (table.columns || []).forEach((col) => {
      emptyRow[col.key] = col.style === 'permission' ? 'EVERYONE' : '';
    });
    updateTable(tableIdx, { rows: [...(table.rows || []), emptyRow] });
  };

  return (
    <div className="admin-form">
      <label>
        Section intro (optional)
        <textarea
          value={section.intro || ''}
          onChange={(e) => update({ intro: e.target.value })}
          rows={2}
          placeholder="Intro text above all tables…"
        />
      </label>

      <div className="tpb-field-group">
        <div className="tpb-field-group-head">
          <strong>Tables</strong>
          <button
            type="button"
            className="btn btn-secondary tpb-add-btn"
            onClick={() => update({ tables: [...tables, defaultTableBlock()] })}
          >
            <i className="fas fa-plus" aria-hidden="true" /> Add table
          </button>
        </div>

        {tables.map((table, tIdx) => (
          <div className="tpb-repeat-block stream-table-editor-block" key={tIdx}>
            <div className="stream-table-editor-block__head">
              <strong>Table {tIdx + 1}</strong>
              <div className="stream-table-editor-block__actions">
                <button
                  type="button"
                  className="admin-icon-btn"
                  title="Move up"
                  disabled={tIdx === 0}
                  onClick={() => update({ tables: moveItem(tables, tIdx, -1) })}
                >
                  <i className="fas fa-arrow-up" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="admin-icon-btn"
                  title="Move down"
                  disabled={tIdx === tables.length - 1}
                  onClick={() => update({ tables: moveItem(tables, tIdx, 1) })}
                >
                  <i className="fas fa-arrow-down" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="admin-icon-btn danger"
                  title="Remove table"
                  onClick={() => update({ tables: tables.filter((_, i) => i !== tIdx) })}
                >
                  <i className="fas fa-trash" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="admin-grid-2">
              <label>
                Table heading
                <input
                  value={table.heading || ''}
                  onChange={(e) => updateTable(tIdx, { heading: e.target.value })}
                  placeholder="Sound"
                />
              </label>
              <label>
                Highlight
                <input
                  value={table.highlight || ''}
                  onChange={(e) => updateTable(tIdx, { highlight: e.target.value })}
                  placeholder="FX"
                />
              </label>
            </div>

            <label>
              Table intro
              <textarea
                value={table.intro || ''}
                onChange={(e) => updateTable(tIdx, { intro: e.target.value })}
                rows={2}
              />
            </label>

            <div className="admin-grid-2">
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={!!table.separatorBefore}
                  onChange={(e) => updateTable(tIdx, { separatorBefore: e.target.checked })}
                />
                Separator before this table
              </label>
              <label>
                Separator label
                <input
                  value={table.separatorLabel || ''}
                  onChange={(e) => updateTable(tIdx, { separatorLabel: e.target.value })}
                  placeholder="// SOUND_FX"
                  disabled={!table.separatorBefore}
                />
              </label>
            </div>

            <div className="admin-grid-2">
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={table.collapsible !== false}
                  onChange={(e) => updateTable(tIdx, { collapsible: e.target.checked })}
                />
                Collapsible panel
              </label>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={table.defaultOpen !== false}
                  onChange={(e) => updateTable(tIdx, { defaultOpen: e.target.checked })}
                />
                Open by default
              </label>
            </div>

            <div className="tpb-field-group">
              <div className="tpb-field-group-head">
                <strong>Columns</strong>
                <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => addColumn(tIdx)}>
                  <i className="fas fa-plus" aria-hidden="true" /> Add column
                </button>
              </div>
              {(table.columns || []).map((col, cIdx) => (
                <div className="tpb-repeat-block" key={col.key || cIdx}>
                  <div className="admin-grid-2">
                    <label>
                      Header label
                      <input
                        value={col.label || ''}
                        onChange={(e) => updateColumn(tIdx, cIdx, { label: e.target.value })}
                      />
                    </label>
                    <label>
                      Cell style
                      <select
                        value={col.style || 'text'}
                        onChange={(e) => updateColumn(tIdx, cIdx, { style: e.target.value })}
                      >
                        {COLUMN_STYLES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label>
                    Column key
                    <input
                      value={col.key || ''}
                      onChange={(e) => updateColumn(tIdx, cIdx, { key: slugifyColumnKey(e.target.value) })}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-secondary tpb-add-btn"
                    onClick={() => removeColumn(tIdx, cIdx)}
                    disabled={(table.columns || []).length <= 1}
                  >
                    Remove column
                  </button>
                </div>
              ))}
            </div>

            <div className="tpb-field-group">
              <div className="tpb-field-group-head">
                <strong>Rows</strong>
                <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => addRow(tIdx)}>
                  <i className="fas fa-plus" aria-hidden="true" /> Add row
                </button>
              </div>
              {(table.rows || []).map((row, rIdx) => (
                <div className="tpb-repeat-block" key={rIdx}>
                  <div className="stream-table-editor-block__head">
                    <strong>Row {rIdx + 1}</strong>
                    <button
                      type="button"
                      className="admin-icon-btn danger"
                      onClick={() => updateTable(tIdx, { rows: table.rows.filter((_, i) => i !== rIdx) })}
                    >
                      <i className="fas fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                  {(table.columns || []).map((col) => (
                    <label key={col.key}>
                      {col.label || col.key}
                      <input
                        value={row[col.key] ?? ''}
                        onChange={(e) => updateRow(tIdx, rIdx, col.key, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
